export class TemplateEngine {
  /**
   * Escapa HTML para evitar XSS almacenado. Toda interpolación {{campo}} se escapa por defecto;
   * para insertar HTML crudo intencional (armado por vos, no de datos de usuario) usar {{{campo}}}.
   */
  static escapeHtml(value) {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  static render(template, data = {}) {
    let result = template;
    result = TemplateEngine.processLoops(result, data);

    Object.keys(data).forEach((key) => {
      // Triple llave = HTML crudo; doble llave = escapado. El crudo primero para que {{{k}}}
      // no sea "comido" por el patrón de {{k}}.
      result = result.replace(new RegExp("{{{" + key + "}}}", "g"), () => data[key] ?? "");
      result = result.replace(new RegExp("{{" + key + "}}", "g"), () => TemplateEngine.escapeHtml(data[key]));
    });

    result = result.replace(/{{{(\w+)\.(\w+)}}}/g, (match, obj, prop) => {
      return data[obj] && data[obj][prop] != null ? data[obj][prop] : "";
    });
    result = result.replace(/{{(\w+)\.(\w+)}}/g, (match, obj, prop) => {
      return data[obj] && data[obj][prop] != null ? TemplateEngine.escapeHtml(data[obj][prop]) : "";
    });

    result = result.replace(/{{{(\w+)\.(\d+)}}}/g, (match, arrayName, index) => {
      return data[arrayName] && data[arrayName][index] != null ? data[arrayName][index] : "";
    });
    result = result.replace(/{{(\w+)\.(\d+)}}/g, (match, arrayName, index) => {
      return data[arrayName] && data[arrayName][index] != null ? TemplateEngine.escapeHtml(data[arrayName][index]) : "";
    });

    result = TemplateEngine.processConditionals(result, data);
    result = result.replace(/{{[^}]+}}/g, "");
    return result;
  }

  /**
   * Expande los bloques {{#each}}, incluidos los anidados a cualquier profundidad.
   * El cierre se busca contando aperturas (findEachClose): un regex no-greedy cerraría
   * el each externo en el {{/each}} del interno.
   */
  static processLoops(template, data) {
    const openRegex = /{{#each\s+(\w+)}}/;
    let result = "";
    let rest = template;

    while (true) {
      const open = openRegex.exec(rest);
      if (!open) return result + rest;

      result += rest.slice(0, open.index);
      const bodyStart = open.index + open[0].length;
      const close = TemplateEngine.findEachClose(rest, bodyStart);
      // Sin cierre el bloque está mal escrito: se deja crudo y lo limpia render().
      if (!close) return result + rest.slice(open.index);

      result += TemplateEngine.expandEach(open[1], rest.slice(bodyStart, close.bodyEnd), data);
      rest = rest.slice(close.blockEnd);
    }
  }

  /** Ubica el {{/each}} que cierra el bloque abierto en bodyStart, salteando los anidados. */
  static findEachClose(text, bodyStart) {
    const tokenRegex = /{{#each\s+\w+}}|{{\/each}}/g;
    tokenRegex.lastIndex = bodyStart;
    let depth = 1;
    let token;
    while ((token = tokenRegex.exec(text)) !== null) {
      if (token[0].startsWith("{{#each")) depth++;
      else if (--depth === 0) return { bodyEnd: token.index, blockEnd: token.index + token[0].length };
    }
    return null;
  }

  static expandEach(arrayName, body, data) {
    const array = Array.isArray(data[arrayName]) ? data[arrayName] : [];

    return array.map((item, index) => {
      const esObjeto = typeof item === "object" && item !== null;
      // El item hereda del contexto padre: un {{campo}} de la raíz tiene que seguir resolviendo
      // adentro del each (y adentro de los anidados).
      const context = esObjeto ? { ...data, ...item } : data;
      let itemResult = TemplateEngine.processLoops(body, context);

      if (esObjeto) {
        Object.keys(item).forEach((key) => {
          itemResult = itemResult.replace(new RegExp("{{{" + key + "}}}", "g"), () => item[key] ?? "");
          itemResult = itemResult.replace(new RegExp("{{" + key + "}}", "g"), () => TemplateEngine.escapeHtml(item[key]));
        });
      } else {
        itemResult = itemResult.replace(/{{{this}}}/g, () => String(item));
        itemResult = itemResult.replace(/{{this}}/g, () => TemplateEngine.escapeHtml(item));
      }

      itemResult = itemResult.replace(/{{@index}}/g, index);
      return TemplateEngine.processConditionals(itemResult, context);
    }).join("");
  }

  static processConditionals(template, data) {
    let result = template;

    const processIfStatements = (text, context) => {
      let hasConditionals = true;
      let iterations = 0;
      const maxIterations = 10;
      while (hasConditionals && iterations < maxIterations) {
        hasConditionals = false;
        iterations++;
        let match;
        const ifRegex = /{{#if\s+([\w.]+)}}((?:(?!{{#if)[\s\S])*?){{\/if}}/g;
        while ((match = ifRegex.exec(text)) !== null) {
          const [fullMatch, condition, content] = match;
          const shouldShow = TemplateEngine.evaluateCondition(condition, context);
          text = text.replace(fullMatch, () => (shouldShow ? content : ""));
          hasConditionals = true;
          break;
        }
      }
      return text;
    };

    const processUnlessStatements = (text, context) => {
      const unlessRegex = /{{#unless\s+([\w.]+)}}([\s\S]*?){{\/unless}}/g;
      return text.replace(unlessRegex, (match, condition, content) => {
        return !TemplateEngine.evaluateCondition(condition, context) ? content : "";
      });
    };

    result = processIfStatements(result, data);
    result = processUnlessStatements(result, data);
    return result;
  }

  static evaluateCondition(condition, context) {
    if (Object.prototype.hasOwnProperty.call(context, condition)) {
      const value = context[condition];
      if (value === false || value === null || value === undefined || value === "" || value === 0) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === "object" && value !== null && Object.keys(value).length === 0) return false;
      return true;
    }
    if (condition.includes(".")) {
      const parts = condition.split(".");
      let current = context;
      for (const part of parts) {
        if (current && Object.prototype.hasOwnProperty.call(current, part)) { current = current[part]; }
        else { return false; }
      }
      return TemplateEngine.evaluateCondition("value", { value: current });
    }
    return false;
  }

  static helpers = {
    formatCurrency: (amount) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(amount),
    formatDate: (date) => new Date(date).toLocaleDateString("es-AR"),
    truncate: (text, length = 50) => (text.length > length ? text.substring(0, length) + "..." : text),
  };
}
